import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Table from '../../../components/material-ui/adaptive/Table';
import MyTableBoldCell from '../../../components/material-ui/adaptive/MyTableBoldCell';
import { useTranslation } from 'react-i18next';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import { getFormatDate, getReadOnlyScheduler } from '../../../utils';
import {
  ENUM_SCHEDULER_STATUS,
  StatusNameByStatus,
  TEST_MODE,
  timezones,
  weekDaysEn
} from '../../../enums/enums';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import BtnEdit from '../../../components/bonus/ui/BtnEdit';
import BtnTrash from '../../../components/bonus/ui/BtnTrash';
import { Link } from 'react-router';
import { API_MAILING } from '../../../api/mailing';
import CusLink from '../../../components/material-ui/CusLink';
import useToast from '../../../hooks/useToast';
import DeleteDialog from '../../../components/material-ui/dialog/DeleteDialog';
import ShowContentWitchEmpty from '../../../components/material-ui/show_content_empty/ShowContentWitchEmpty';
import BtnView from '../../../components/bonus/ui/BtnView';
import { mockMailing } from '../../../enums/mock';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import BtnCopy from '../../../components/bonus/ui/BtnCopy';
import { IconTooltip } from '@tabler/icons-react';
import CusItemTooltip from '../../../components/material-ui/CusItemTooltip';
import CusButton from '../../../components/material-ui/CusButton';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import { useAppSelector } from '../../../store';
import { useDispatch } from 'react-redux';
import { setMailings, setMailingsFilter } from '../../../store/apps/schedulerSlice';
import TypographyText from '../../../components/material-ui/table/TypographyText';

export default function SchedulerTable() {
  const { t } = useTranslation();
  const { successToast } = useToast();
  const { isMobile } = useBootstrapSize();
  const dispatch = useDispatch();
  const mailingsFilter = useAppSelector((state) => state.scheduler.mailingsFilter);
  const mailings = useAppSelector((state) => state.scheduler.mailings);

  const [mailingStatus, setMailingStatus] = useState(mailingsFilter);
  const [isNotMailing, setIsNotMailing] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const actions = ['copy', 'edit', 'delete'];

  const getMailingList = () => {
    API_MAILING.list
      .mailing({ statusList: mailingStatus })
      .then((e) => {
        const list = e.data.list;
        dispatch(setMailingsFilter(mailingStatus));
        dispatch(setMailings(list));
        setIsNotMailing(!list.length);
      })
      .catch(console.error);
  };

  useEffect(() => {
    if (TEST_MODE) {
      dispatch(setMailings(mockMailing));
      return;
    }

    getMailingList();
  }, []);

  const DateSend = ({ date, days, time }) => {
    if (date) return getFormatDate(date, 'DD.MM.YYYY HH:mm');

    const lastDayIndx = days?.lastIndexOf(true);

    return (
      <Typography fontSize={12}>
        {days?.map((isDay, indxDay) => {
          if (!isDay) return '';

          const comma = indxDay < lastDayIndx ? ', ' : '';
          return t(weekDaysEn[indxDay]) + comma;
        })}

        <ShowContainer condition={time}>
          {' '}
          {t('in')} {time}
        </ShowContainer>
      </Typography>
    );
  };

  const onDeleteMailing = (id) => {
    API_MAILING.delete({ mailingId: id })
      .then((e) => {
        const newMailings = structuredClone(mailings);
        const deletedMailing = newMailings.find((el) => el.id == id);
        deletedMailing.status = ENUM_SCHEDULER_STATUS.deleted;
        dispatch(setMailings(newMailings));
        successToast(t('The mailing list has been successfully deleted'));
      })
      .catch(console.error);
  };

  const MailingAction = ({ action, status, id }) => {
    const readOnly = getReadOnlyScheduler(status);
    if (status === ENUM_SCHEDULER_STATUS.deleted) return;

    if (readOnly && action == 'edit') {
      return (
        <BtnView sx={{ minWidth: 50, width: 50 }} LinkComponent={Link} to={`/mailing/${id}`} />
      );
    }

    // if (action == 'edit' && status !== ENUM_SCHEDULER_STATUS.inactive)
    if (action == 'edit') {
      return (
        <BtnEdit sx={{ minWidth: 50 }} iconSize={16} LinkComponent={Link} to={`/mailing/${id}`} />
      );
    }

    if (action == 'delete') {
      return <BtnTrash sx={{ minWidth: 50 }} iconSize={16} onDelete={() => setOpenDelete(id)} />;
    }

    if (action == 'copy')
      return (
        <Tooltip title={t('clone')}>
          <BtnCopy
            sx={{ minWidth: 50 }}
            iconSize={16}
            LinkComponent={Link}
            to={`/mailing/new?clone=${id}`}
          />
        </Tooltip>
      );
  };

  const mailingStatusOptions = [
    { label: 'Active', value: ENUM_SCHEDULER_STATUS.active },
    { label: 'Inactive', value: ENUM_SCHEDULER_STATUS.inactive },
    { label: 'Progress', value: ENUM_SCHEDULER_STATUS.progress },
    { label: 'Deleted', value: ENUM_SCHEDULER_STATUS.deleted }
  ];

  return (
    <>
      <Stack gap={2}>
        <Stack gap={1} direction={isMobile ? 'column' : 'row'} alignItems={'end'}>
          <Box width={300} maxWidth={'calc(100svw - 64px)'}>
            <CusLabelSelect
              label={'Mailing list status'}
              options={mailingStatusOptions}
              multi
              value={mailingStatus}
              onChange={(val) => setMailingStatus(val)}
            />
          </Box>

          <CusButton text="Find" onClick={getMailingList} sx={{ px: 5 }} variant="contained" />
        </Stack>

        <ShowContentWitchEmpty
          condition={mailings.length}
          conditionPlug={isNotMailing}
          plug={
            <Typography component={'span'} mt={2}>
              {t('No mailings found.')} <CusLink text="Create a mailing." to={`/mailing/new`} />
            </Typography>
          }>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <MyTableBoldCell isLess>{t('ID')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Status')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Mailing name')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Group')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Time zone')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Active, from to')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Sending')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Recipients')}</MyTableBoldCell>
                  <MyTableBoldCell isLess>{t('Author')}</MyTableBoldCell>
                  <MyTableBoldCell isLess></MyTableBoldCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mailings.map((mailing, indx) => (
                  <TableRow key={indx}>
                    <MyTableCell isLess>{mailing.id}</MyTableCell>
                    <MyTableCell isLess>{t(mailing.status)}</MyTableCell>
                    <MyTableCell isLess>{mailing.title}</MyTableCell>
                    <MyTableCell isLess>
                      <CusLink text={mailing.groupName} to={`/groups/${mailing.groupId}`} />
                    </MyTableCell>
                    <MyTableCell isLess>
                      {timezones.find((el) => el.value == mailing.groupTzOffset)?.label}
                    </MyTableCell>
                    <MyTableCell isLess>
                      {getFormatDate(mailing.sendDateFrom)} - {getFormatDate(mailing.sendDateTo)}
                    </MyTableCell>
                    <MyTableCell isLess>
                      <DateSend
                        date={mailing.sendAtDate}
                        days={mailing.sendByDays}
                        time={mailing.sendAtTime}
                      />
                    </MyTableCell>
                    <MyTableCell isLess type="numeric">
                      {mailing.userCount}
                    </MyTableCell>
                    <MyTableCell isLess>
                      <CusLink text={mailing.authorLogin} to={`/users/logs/${mailing.authorId}`} />
                    </MyTableCell>
                    <MyTableCell isLess>
                      <Stack gap={1} direction={'row'}>
                        {actions?.map((action) => (
                          <MailingAction
                            action={action}
                            id={mailing.id}
                            key={action}
                            status={mailing.status}
                          />
                        ))}
                      </Stack>
                    </MyTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </ShowContentWitchEmpty>
      </Stack>

      <DeleteDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={() => onDeleteMailing(openDelete)}
        content={t('Are you sure you want to delete newsletter #? This action is irreversible.', {
          numberId: openDelete
        })}
      />
    </>
  );
}
